/* eslint no-console: ["error", { allow: ["error"] }] */
import { createAsyncThunk } from "@reduxjs/toolkit";
import GeoJSON, { MultiPolygon, Polygon } from "geojson";
import { AxiosError, AxiosResponse } from "axios";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { aggregatePagedResponse } from "@iventis/api-helpers/src/api-helpers";
import { UpdateMultipleMapObjects } from "@iventis/domain-model/model/updateMultipleMapObjects";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import type { Map } from "@iventis/domain-model/model/map";
import { DataField } from "@iventis/domain-model/model/dataField";
import { TypedFeature } from "@iventis/map-engine/src/types/internal";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { Terrain } from "@iventis/domain-model/model/terrain";
import { Asset } from "@iventis/domain-model/model/asset";
import { SavedMapView } from "@iventis/domain-model/model/savedMapView";
import { GeometryType } from "@iventis/domain-model/model/geometryType";
import { MapComment } from "@iventis/domain-model/model/mapComment";
import qs from "qs";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { convertDateDatafieldValuesToUTC, convertDateDatafieldValuesToLocal, uuidOnlyObjectEntries, parseDataFieldValuesWithRepeatedTimeRange } from "@iventis/utilities";
import { convertMapObjectToLocalGeoJson } from "@iventis/map-engine/src/utilities/state-helpers";
import { parseISO } from "date-fns";
import { CreateMapRequestPayload, DomainLayer, MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { OptionalExceptFor } from "@iventis/types/useful.types";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { mappingApi, assetsApi } from "@iventis/api/src/api";
import { ResponseError } from "@iventis/api/src/axios.types";
import { DEFAULT_COMMENT_ICON_VALUE, commentsLayerId } from "@iventis/map-engine";
import { incrementMonitoringCount, layerCreationMonitoringMetric } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { LocalGeoJsonObject, MapCommentProperties, MapObjectProperties, SelectedMapObject } from "@iventis/map-types";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { getMapObjectQuantityOfModelsInAreaSystemDataField, getMapObjectRotationZOfModelsInAreaSystemDataField } from "@iventis/datafields";
import { AuthState } from "@iventis/permissions/src/auth.slice";
import { map } from "./map.slice";
import { mergeBoundsWithTileOnlyObjects } from "./map.slice.functions";
import { SidebarOrderUpdate } from "../sidebar-order-helpers";
import { deleteLayerDataField, postLayerDataField } from "../services/data-field-service";
import { patchLayers, postLayer } from "../services/layer-service";
import { routeWaypointService } from "../services/route-waypoint-service";
import { eventStream } from "../event-stream";

type RootState = { mapReducer: MapGlobalState; auth: AuthState };

export const createMapRequest = createAsyncThunk<Map, CreateMapRequestPayload>("map/createMapRequest", async (map, { rejectWithValue }) => {
    try {
        const response = await mappingApi.post<Map>(
            "/maps",
            { type: NodeType.Map, ...map },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.plan) }),
                    message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.plan) }),
                },
            }
        );
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const createLayerRequest = createAsyncThunk<MapLayer, MapLayer, { getState; state: { mapReducer: MapGlobalState } }>(
    "map/createLayerRequest",
    async (layer, { rejectWithValue, getState }) => {
        try {
            const response = await postLayer(layer, getState().mapReducer.mapModule.layers.value);
            layerCreationMonitoringMetric(layer.styleType, { resourceId: getState().mapReducer.mapModule.mapId });
            return response;
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            console.error("Error", error);
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateMap = createAsyncThunk("Map/updateMap", async (options: { map: Partial<Map>; skipSave?: boolean }, { rejectWithValue }) => {
    try {
        if (options.skipSave) {
            return { map: options.map };
        }
        const response = await mappingApi.patch<Map>(`/maps/${options.map.id}`, options.map, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.plan) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.plan) }),
            },
        });
        return { map: response.data };
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export type MapLayerPatchType = Partial<Omit<MapLayer, "dataFields">> & Pick<MapLayer, "id">;

export const patchMapLayers = createAsyncThunk<DomainLayer[], MapLayerPatchType[], { state: RootState }>(
    "Map/patchMapLayers",
    async (mapLayers: MapLayerPatchType[], { rejectWithValue, getState, dispatch }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;

            // Capture previous layer styles to detect style changes
            const previousLayers = getState().mapReducer.mapModule.layers.value;
            const styleChanges = mapLayers
                .filter((p) => p.styleType !== undefined)
                .map((p) => ({ patch: p, before: previousLayers.find((pl) => pl.id === p.id) }))
                .filter((s) => s.before && s.before.styleType !== s.patch.styleType);

            await patchLayers(mapId, mapLayers);

            // After successful patch, trigger any style-change side effects
            for (const change of styleChanges) {
                // If new style is AreaModel, refresh geojson for local objects on that layer
                if (change.patch.styleType === StyleType.AreaModel) {
                    const localObjects = getState().mapReducer.mapModule.geoJSON.value[change.patch.id] ?? [];
                    if (localObjects.length) {
                        await dispatch(
                            getGeoJson({
                                objects: localObjects.map((o) => ({ objectId: o.objectId, layerId: change.patch.id })),
                                mapId,
                                layers: getState().mapReducer.mapModule.layers.value,
                            })
                        );
                    }
                }
            }

            return getState().mapReducer.mapModule.layers.value.reduce<DomainLayer[]>((cum, lyr) => {
                const layer = mapLayers.find(({ id }) => lyr.id === id);
                return layer ? [...cum, { ...lyr, ...layer }] : cum;
            }, []);
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            // eslint-disable-next-line no-console
            console.error("Error", error);
            return rejectWithValue(error.response.data);
        }
    }
);
export const createLayerDataFieldThunk = createAsyncThunk<DataField, { dataField: DataField; layerId: string }, { state: RootState }>(
    "Map/createLayerDataField",
    async (request, { rejectWithValue, getState }) => {
        try {
            let { order } = request.dataField;
            if (order == null || order === 0) {
                const existingLayerDataFields = getState().mapReducer.mapModule.layers.value.find((l) => l.id === request.layerId)?.dataFields;
                order = existingLayerDataFields?.length + 1;
            }
            incrementMonitoringCount({ name: MetricName.CREATE_DATAFIELD, resourceId: request.dataField.id });
            const response = await postLayerDataField({ ...request, dataField: { ...request.dataField, order } });
            return response;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const updateLayerDataFieldThunk = createAsyncThunk<DataField, { dataField: DataField; layerId: string }>(
    "Map/updateLayerDataField",
    async (request, { rejectWithValue }) => {
        try {
            const response = await mappingApi.patch<DataField>(`/layers/${request.layerId}/data-fields/${request.dataField.id}`, request.dataField, {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.layer_data_field) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.layer_data_field) }),
                },
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteLayerDataFieldThunk = createAsyncThunk<string, { dataFieldId: string; layerId: string }, { state: RootState }>(
    "Map/deleteLayerDataField",
    async (request, { rejectWithValue }) => {
        try {
            const response = await deleteLayerDataField(request);
            incrementMonitoringCount({ name: MetricName.DELETE_DATAFIELD, resourceId: request.dataFieldId });
            return response;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export interface GetGeoJsonResponse {
    feature: TypedFeature;
    objectId: string;
    layerId: string;
    waypoints: RouteWaypoint[] | undefined;
}

export const getGeoJson = createAsyncThunk<GetGeoJsonResponse[], { objects: { layerId: string; objectId: string }[]; mapId: string; layers: DomainLayer[] }>(
    "map/getGeoJson",
    async ({ objects, mapId, layers }, { rejectWithValue }) => {
        try {
            const objectsWithGeoJson = await Promise.all<GetGeoJsonResponse>(
                objects.map((object) =>
                    mappingApi.get<TypedFeature>(`maps/${mapId}/map_objects/${object.objectId}/geojson`).then((response) => {
                        const filteredLayers = layers.find((l) => l.id === object.layerId);
                        return {
                            feature: {
                                type: "Feature",
                                properties: convertDateDatafieldValuesToLocal({ ...response.data.properties, layerid: object.layerId }, filteredLayers?.dataFields),
                                geometry: response.data.geometry,
                            } as TypedFeature,
                            objectId: object.objectId,
                            layerId: object.layerId,
                            waypoints: undefined,
                        };
                    })
                )
            );
            // Assign their waypoints if they are routes
            for (let i = 0; i < objectsWithGeoJson.length; i += 1) {
                const object = objectsWithGeoJson[i];
                if (object.feature.properties.modeOfTransport != null) {
                    object.waypoints = await routeWaypointService.getRouteWaypoints(object.objectId, mapId);
                } else {
                    object.waypoints = undefined;
                }
            }

            return objectsWithGeoJson;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

// eslint-disable-next-line consistent-return
export const createObjects = createAsyncThunk<{ mapObjects: ObjectUpdate[]; mapObjectBounds: Polygon | MultiPolygon | undefined }, ObjectUpdate[], { state: RootState }>(
    "map/createObject",
    async (objects, { rejectWithValue, getState }) => {
        try {
            const { mapId, layers, tileSources, bounds } = getState().mapReducer.mapModule;

            /** Maps a layer id to its layer to prevent repeated looping through layers */
            const layerIdToLayerMapping = new Map<string, MapLayer>();

            /** Objects which have tile only features on them (models in an area) */
            const tileOnlyObjects: ObjectUpdate[] = [];

            const mapObjects = objects.map((object) => {
                if (!layerIdToLayerMapping.has(object.layerId)) {
                    const layer = layers?.value?.find((l) => l.id === object.layerId);
                    layerIdToLayerMapping.set(object.layerId, layer);
                }
                if (layerIdToLayerMapping.get(object.layerId)?.styleType === StyleType.AreaModel) {
                    tileOnlyObjects.push(object);
                }
                const layerDataFields = layerIdToLayerMapping.get(object.layerId)?.dataFields ?? [];
                // Derive the data field values from the layer data fields in case the object has out of date data field values
                let dataFieldValues = layerDataFields.reduce<Record<string, unknown>>((acc, dataField) => {
                    if (object.properties[dataField.id] != null) {
                        acc[dataField.id] = object.properties[dataField.id];
                    }
                    return acc;
                }, {});
                // Ensure all time-related data field values are in UTC
                dataFieldValues = convertDateDatafieldValuesToUTC(dataFieldValues, layerDataFields);
                return {
                    id: object.objectId,
                    layerId: object.layerId,
                    geoJsonFeature: { type: "Feature", geometry: object.geometry, properties: object.properties },
                    dataFieldValues,
                    fixedShape: object.properties.fixedShape,
                    level: object.properties.level,
                    modeOfTransport: object.properties.modeOfTransport,
                    sitemapId: object.properties.sitemapId,
                } as MapObject;
            });
            await mappingApi.post<MapObject[]>(`/maps/${mapId}/map_objects`, mapObjects, {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.map_objects) }),
                    message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.map_objects) }),
                },
            });

            // Update the waypoints if any of the objects are routes
            objects.forEach(async (update) => {
                if (update.waypoints != null) {
                    await routeWaypointService.putRouteWaypoints(update.objectId, mapId, update.waypoints);
                }
            });

            const mapObjectBounds = mergeBoundsWithTileOnlyObjects(tileSources.value.objects.bounds, tileOnlyObjects, bounds.value.bounds);
            // If the bounds don't change, we still need to update the tile source
            if (!mapObjectBounds && tileOnlyObjects.length > 0) {
                eventStream.next({ type: "TILE_ONLY_MAP_OBJECTS_UPDATED", objects: tileOnlyObjects });
            }

            return { mapObjects: objects, mapObjectBounds };
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const restoreObjects = createAsyncThunk<{ mapObjects: MapObject[]; mapObjectBounds: Polygon | MultiPolygon | undefined }, ObjectUpdate[], { state: RootState }>(
    "map/restoreObjects",
    async (objects: ObjectUpdate[], { rejectWithValue, getState }) => {
        try {
            const { mapId, layers, tileSources, bounds } = getState().mapReducer.mapModule;
            /** Maps a layer id to its layer to prevent repeated looping through layers */
            const layerIdToLayerMapping = new Map<string, MapLayer>();

            /** Objects which have tile only features on them (models in an area) */
            const tileOnlyObjects: ObjectUpdate[] = [];

            const mapObjects = objects.map((object) => {
                if (!layerIdToLayerMapping.has(object.layerId)) {
                    const layer = layers?.value?.find((l) => l.id === object.layerId);
                    layerIdToLayerMapping.set(object.layerId, layer);
                }
                const layer = layerIdToLayerMapping.get(object.layerId);
                if (layer?.styleType === StyleType.AreaModel) {
                    tileOnlyObjects.push(object);
                }
                return {
                    id: object.objectId,
                    layerId: object.layerId,
                    geoJsonFeature: {
                        type: object.geometry.type,
                        coordinates: object.geometry.coordinates,
                        rotation: object.properties.rotation,
                        properties: object.properties,
                    },
                    dataFieldValues: convertDateDatafieldValuesToUTC(uuidOnlyObjectEntries(object.properties), layer?.dataFields),
                    fixedShape: object.properties.fixedShape,
                    level: object.properties.level,
                    modeOfTransport: object.properties.modeOfTransport,
                } as MapObject;
            });

            await mappingApi.put<string[]>(
                `/maps/${mapId}/map_objects/restore`,
                mapObjects.map((o) => o.id),
                {
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_objects) }),
                        message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_objects) }),
                    },
                }
            );

            // Update the waypoints if any of the objects are routes
            objects.forEach(async (update) => {
                if (update.waypoints != null) {
                    await routeWaypointService.putRouteWaypoints(update.objectId, mapId, update.waypoints);
                }
            });

            const mapObjectBounds = mergeBoundsWithTileOnlyObjects(tileSources.value.objects.bounds, tileOnlyObjects, bounds.value.bounds);
            // If the bounds don't change, we still need to update the tile source
            if (!mapObjectBounds && tileOnlyObjects.length > 0) {
                eventStream.next({ type: "TILE_ONLY_MAP_OBJECTS_UPDATED", objects: tileOnlyObjects });
            }

            return { mapObjects, mapObjectBounds };
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteObjects = createAsyncThunk<string[], { mapObjects: SelectedMapObject[]; modifyStore: boolean }, { getState; state: { mapReducer: MapGlobalState } }>(
    "map/deleteObjects",
    async ({ mapObjects }, { rejectWithValue, dispatch, getState }) => {
        try {
            const { mapId, layers } = getState().mapReducer.mapModule;
            const objectIds = mapObjects.map((object) => object.objectId);
            dispatch(map.actions.addObjectChangesSaving(objectIds));
            // Construct a filter for mapObjectIds
            const filters = [
                {
                    $type: "IventisFilterGuidArray",
                    fieldName: "mapObjectIds",
                    operator: IventisFilterOperator.In,
                    value: objectIds,
                },
            ];
            await mappingApi.delete(`/maps/${mapId}/map_objects`, {
                data: filters,
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.delete.title, { entity: translate(Content.map9.map_objects) }),
                    message: translate(Content.errors.custom_network_error.delete.message, { entity: translate(Content.map9.map_objects) }),
                },
            });
            dispatch(map.actions.removeObjectChangesSaving(objectIds));

            const tileOnlyObjects = mapObjects.filter((object) => {
                const layer = layers?.value?.find((l) => l.id === object.layerId);
                return layer?.styleType === StyleType.AreaModel;
            });

            // If there are tile only map objects we must tell the engine to refresh the source
            if (tileOnlyObjects.length > 0) {
                eventStream.next({ type: "TILE_ONLY_MAP_OBJECTS_UPDATED", objects: tileOnlyObjects });
            }
            return objectIds;
        } catch (err) {
            console.error(err);
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteObjectsByLayerIds = createAsyncThunk<string[], { layerIds: string[] }, { getState; state: { mapReducer: MapGlobalState } }>(
    "map/deleteObjectsByLayerId",
    async ({ layerIds }, { rejectWithValue, getState }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;
            // Construct a filter for layerIds
            const filters = [
                {
                    $type: "IventisFilterGuidArray",
                    fieldName: "layerIds",
                    operator: IventisFilterOperator.In,
                    value: layerIds,
                },
            ];
            await mappingApi.delete(`/maps/${mapId}/map_objects`, {
                data: filters,
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.delete.title, { entity: translate(Content.map9.map_objects) }),
                    message: translate(Content.errors.custom_network_error.delete.message, { entity: translate(Content.map9.map_objects) }),
                },
            });
            return layerIds;
        } catch (err) {
            console.error(err);
            return rejectWithValue(err.response.data);
        }
    }
);

export const patchMapObjectsDataFieldValues = createAsyncThunk<
    MapObject[],
    {
        mapObjects: OptionalExceptFor<MapObject, "id" | "layerId">[];
        signal?: AbortSignal;
        from?: "data-table" | undefined;
        fulfilledNotification?: boolean;
        skipReflectLocalSource?: boolean;
    },
    { state: RootState }
>("map/patchMapObjectsDataFieldValues", async ({ mapObjects, signal }, { rejectWithValue, getState }) => {
    try {
        if (mapObjects?.length === 0 || mapObjects === undefined) {
            return [];
        }
        const { mapId, layers, projectDataFields } = getState().mapReducer.mapModule;

        const tileObjects: SelectedMapObject[] = [];

        // Convert map object dates to UTC
        const mapObjectsPatch = mapObjects.map((object) => {
            const layer = layers?.value?.find((l) => l.id === object.layerId);
            // All area models have tile only objects on them (models), but we only want to refresh the tiles here if rotation or quantity has changed
            const rotationDf = getMapObjectRotationZOfModelsInAreaSystemDataField(projectDataFields);
            const quantityDf = getMapObjectQuantityOfModelsInAreaSystemDataField(projectDataFields);
            const tileModelsRequireRefresh = Object.keys(object.dataFieldValues).some((id) => [rotationDf?.id, quantityDf?.id].includes(id));
            if (layer?.styleType === StyleType.AreaModel && tileModelsRequireRefresh) {
                tileObjects.push({ objectId: object.id, layerId: object.layerId });
            }
            // If repeated time range values are present they need to be converted, if value is not repeated time range it will be returned as is
            const objDF = parseDataFieldValuesWithRepeatedTimeRange(object?.dataFieldValues);

            return {
                ...object,
                dataFieldValues: convertDateDatafieldValuesToUTC(objDF, layer?.dataFields),
            };
        });
        const response = await mappingApi.patch<MapObject[]>(`/maps/${mapId}/map_objects/data-fields`, mapObjectsPatch, {
            signal,
            suppressErrors: true,
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_objects_attribute_values) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_objects_attribute_values) }),
            },
        });

        // If there are tile only map objects (i.e. the models inside area of 3d models), we need to refresh the source
        if (tileObjects.length > 0) {
            eventStream.next({ type: "TILE_ONLY_MAP_OBJECTS_UPDATED", objects: tileObjects });
        }
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const uploadGeometry = createAsyncThunk<{ mapObjectBounds: undefined | Polygon | MultiPolygon }, ObjectUpdate[], { getState; state: { mapReducer: MapGlobalState } }>(
    "map/uploadGeometry",
    async (objects, { getState }) => {
        const {
            mapModule: { mapId, layers, tileSources, bounds },
            creatingObjects,
        } = getState().mapReducer;

        let mapObjectBounds: Polygon | MultiPolygon | undefined;

        const objectsToUpdateNow = objects.filter((o) => !creatingObjects.includes(o.objectId));
        // Only upload geometries if the object has already been created, if it hasn't the middleware will handle the queue
        if (objectsToUpdateNow.length > 0) {
            /** Maps a layer id to its layer to prevent repeated looping through layers */
            const layerIdToLayerMapping = new Map<string, MapLayer>();

            /** Objects which have tile only features on them (models in an area) */
            const tileOnlyObjects: ObjectUpdate[] = [];

            await mappingApi.put<void, AxiosResponse<void>, UpdateMultipleMapObjects>(
                `maps/${mapId}/map_objects/geometry`,
                objectsToUpdateNow.reduce(
                    (cum, object) => {
                        const add = () => ({
                            id: object.objectId,
                            type: object.geometry.type,
                            coordinates: object.geometry.coordinates,
                            rotation: object.properties.rotation,
                            level: object.properties.level,
                            modeOfTransport: object.properties.modeOfTransport,
                            sitemapId: object.properties.sitemapId,
                        });
                        switch (object.geometry.type) {
                            case GeometryType.LineString:
                                return { ...cum, lineStrings: [...cum.lineStrings, add()] };
                            case GeometryType.Polygon: {
                                // Currently tile only objects are on polygons so only track for them for now
                                if (!layerIdToLayerMapping.has(object.layerId)) {
                                    const layer = layers?.value?.find((l) => l.id === object.layerId);
                                    layerIdToLayerMapping.set(object.layerId, layer);
                                }
                                if (layerIdToLayerMapping.get(object.layerId)?.styleType === StyleType.AreaModel) {
                                    tileOnlyObjects.push(object);
                                }
                                return { ...cum, polygons: [...cum.polygons, add()] };
                            }
                            case GeometryType.Point:
                                return { ...cum, points: [...cum.points, add()] };
                            default:
                                return cum;
                        }
                    },
                    { lineStrings: [], polygons: [], points: [] }
                ),
                {
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_object_geometry) }),
                        message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_object_geometry) }),
                    },
                }
            );

            // Update the waypoints if any of the objects are routes
            objectsToUpdateNow.forEach(async (update) => {
                if (update.properties.modeOfTransport != null) {
                    await routeWaypointService.putRouteWaypoints(update.objectId, mapId, update.waypoints);
                }
            });

            mapObjectBounds = mergeBoundsWithTileOnlyObjects(tileSources.value.objects.bounds, tileOnlyObjects, bounds.value.bounds);

            // If the bounds don't change, we still need to update the tile source
            if (!mapObjectBounds && tileOnlyObjects.length > 0) {
                eventStream.next({ type: "TILE_ONLY_MAP_OBJECTS_UPDATED", objects: tileOnlyObjects });
            }
        }
        return { mapObjectBounds };
    }
);

export const getProjectDataFields = createAsyncThunk<DataField[], { signal: AbortSignal }, { state: RootState }>("map/getProjectDataFields", async ({ signal }, { getState }) => {
    const { projectId } = getState().auth.user;
    const dataFields = await mappingApi.get<DataField[]>(`/projects/${projectId}/data-fields`, { signal });
    return dataFields.data;
});

export const getSitemaps = createAsyncThunk<Sitemap[], { signal?: AbortSignal }, { state: RootState }>("map/getSitemaps", async ({ signal }, { getState }) => {
    if (!getState().auth.subscriptionPlan.functionality.siteMaps) {
        return [];
    }

    const sitemaps = await mappingApi.get<Sitemap[]>(`/sitemaps`, {
        signal,
        params: {
            filter: JSON.stringify([{ fieldName: "status", operator: IventisFilterOperator.In, value: ["Active"] }]),
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });
    return sitemaps.data;
});

export const getTerrains = createAsyncThunk<Terrain[], { signal?: AbortSignal }, { state: RootState }>("map/getTerrains", async ({ signal }, { dispatch }) => {
    const terrains = await mappingApi.get<Terrain[]>(`/terrains`, {
        signal,
        params: {
            filter: JSON.stringify([{ fieldName: "status", operator: IventisFilterOperator.In, value: ["Active"] }]),
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });

    dispatch(map.actions.setTerrains(terrains.data));
    dispatch(getTerrainAssets(terrains.data));
    return terrains.data;
});

export const getTerrainAssets = createAsyncThunk<Asset[], Terrain[], { state: RootState }>("map/getTerrainAssets", async (terrains: Terrain[]) => {
    const terrainAssets: Asset[] = [];
    await Promise.all(
        terrains.map(async (terrain: Terrain) => {
            // Get the terrain asset
            const terrainAsset = await assetsApi.get<Asset>(`/assets/${terrain.id}`);
            terrainAssets.push(terrainAsset.data);
        })
    );
    return terrainAssets;
});

export const updateTerrainSignatures = createAsyncThunk<Asset[], Terrain[], { state: RootState }>("map/updateTerrainSignatures", async (terrains, { getState }) => {
    if (!getState().auth.subscriptionPlan.functionality.siteMaps) {
        return [];
    }
    const assets: Asset[] = [];
    await Promise.all(
        terrains.map(async (terrain) => {
            // Get the terrain asset
            const terrainAsset = await assetsApi.get<Asset>(`/assets/${terrain.id}`);
            if (terrainAsset?.data != null) {
                terrainAsset.data.updatedAt = parseISO(terrainAsset.data.updatedAt.toString());
            }
            assets.push(terrainAsset.data);
        })
    );
    return assets;
});

export const updateSavedMapView = createAsyncThunk("Map/updateSavedMapView", async (savedMapView: SavedMapView, { rejectWithValue }) => {
    try {
        const response = await mappingApi.put<SavedMapView>(`/saved_map_views`, savedMapView, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.saved_map_view) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.saved_map_view) }),
            },
        });
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const createSavedMapView = createAsyncThunk("Map/createSavedMapView", async (savedMapView: SavedMapView, { rejectWithValue }) => {
    try {
        const response = await mappingApi.post<SavedMapView>(`/saved_map_views`, savedMapView, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.saved_map_view) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.saved_map_view) }),
            },
        });
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const patchMapBackgroundId = createAsyncThunk("Map/patchMapBackgroundId", async (data: { mapId: string; backgroundId: string }, { rejectWithValue }) => {
    try {
        await mappingApi.patch<Map>(
            `/maps/${data.mapId}`,
            { backgroundId: data.backgroundId },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_background) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_background) }),
                },
            }
        );
        return data.backgroundId;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const patchDefaultSavedMapView = createAsyncThunk("Map/patchDefaultSavedMapView", async (data: { mapId: string; savedMapViewId: string }, { rejectWithValue }) => {
    try {
        await mappingApi.patch<Map>(
            `/maps/${data.mapId}`,
            { defaultSavedMapViewId: data.savedMapViewId },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.saved_map_view) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.saved_map_view) }),
                },
            }
        );
        return data.savedMapViewId;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const getRemoteLayerGeometry = createAsyncThunk<LocalGeoJsonObject[], { layerId: string; signal?: AbortSignal; overwrite?: boolean }, { state: RootState }>(
    "Map/getRemoteLayerGeometry",
    async ({ layerId, signal }, { rejectWithValue, getState }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;
            const filter = {
                params: {
                    filter: JSON.stringify([{ fieldName: "layerId", operator: IventisFilterOperator.In, value: [layerId] }]),
                    mapId,
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
            };
            const res = await aggregatePagedResponse<MapObject[]>(
                mappingApi,
                `/maps/${getState().mapReducer.mapModule.mapId}/map_objects/filter`,
                { ...filter, signal },
                async (cum, newValue) => (cum ?? []).concat(newValue)
            );
            // Get the waypoints for the map objects if they exist
            const waypoints = await Promise.all(
                res.map<Promise<RouteWaypoint[]>>(async (object) => {
                    let waypoints: RouteWaypoint[] | null = null;
                    if ((object.modeOfTransport ?? object.geoJsonFeature?.properties?.modeOfTransport) != null) {
                        waypoints = await routeWaypointService.getRouteWaypoints(object.id, mapId);
                    }
                    return waypoints;
                })
            );
            return res?.map?.((object, index) => convertMapObjectToLocalGeoJson(object, waypoints[index]));
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const updateMapSidebarOrder = createAsyncThunk("Map/updateLayersSidebarOrder", async (data: { layers: SidebarOrderUpdate[]; mapId: string }, { rejectWithValue }) => {
    try {
        const request = await mappingApi.patch(`maps/${data.mapId}/layers`, data.layers, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_sidebar_order) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_sidebar_order) }),
            },
        });
        return request;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const patchMapComment = createAsyncThunk<void, GeoJSON.Feature<GeoJSON.Point, MapObjectProperties>, { state: RootState }>(
    "Map/patchMapComment",
    async (payload, { getState }) => {
        const { mapId } = getState().mapReducer.mapModule;
        await mappingApi.patch<Partial<MapComment>>(
            `/maps/${mapId}/comments/${payload.properties.id}`,
            {
                geometry: payload.geometry,
                level: payload.properties.level,
                sitemapId: payload.properties.sitemapId,
            },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_comment) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_comment) }),
                },
            }
        );
    }
);

export const getMapCommentFeature = createAsyncThunk<
    GeoJSON.Feature<GeoJSON.Point, MapCommentProperties>,
    { commentId: string; focus?: boolean; panTo?: boolean; existsOnServer?: boolean },
    { state: RootState }
>("Map/getMapComment", async ({ commentId, focus }, { getState }) => {
    const { mapId, geoJSON } = getState().mapReducer.mapModule;
    const existingComment = geoJSON.value[commentsLayerId]?.find((c) => c.objectId === commentId);
    if (existingComment != null) {
        return {
            ...existingComment.feature,
            properties: { ...existingComment.feature.properties, imageId: focus ? DEFAULT_COMMENT_ICON_VALUE : existingComment.feature.properties.userId },
        } as GeoJSON.Feature<GeoJSON.Point, MapCommentProperties>;
    }
    const response = await mappingApi.get<MapComment>(`maps/${mapId}/comments/${commentId}`);
    const feature = {
        geometry: response.data.geometry,
        type: "Feature",
        properties: {
            id: response.data.id,
            layerid: commentsLayerId,
            level: response.data.level,
            userId: response.data.userId,
            imageId:
                // Ensure the selection hasn't changed since the request was made
                focus && getState().mapReducer.mapModule.commentsSelected.value.some((c) => c.commentId === response.data.id) ? DEFAULT_COMMENT_ICON_VALUE : response.data.userId,
            rotation: undefined,
            order: focus ? 1 : 0,
        },
    } as const;
    return feature;
});
